/**
 * RunCapsule — the mini spine. One drafting run is ONE card in the Chat
 * tab, a miniature of the Spine, instead of one chat message per drafted
 * section.
 *
 * Same look as the launch-film Spine (LoomSpine): the "Drafting · N
 * sections" header with elapsed / total time and the per-state tally, the
 * thin violet progress bar, and rows with a mono number, a status chip that
 * carries the three stage dashes (Sources / Drafting / Review), the newest
 * agent action as the second line, running rows on the brand-tinted band
 * with a rail and a bold timer, and the green check once a row is done.
 *
 * Live (running / paused): a short window of rows — every running row, one
 * either side, the rest folded into "N done" / "N queued". Settled (done /
 * failed): the header, the bar, and only the sections that need review.
 * Rows open the section in the Spine; "Open Spine" switches tabs.
 *
 * SRP: presentation only. Rows come from the shell's scoped spine list (the
 * same array the Spine tab renders), so the card and the Spine always agree
 * on scope and status. The only local state is the stage memo and the
 * per-row timers, mirroring LoomSpine.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { ChevronRight12Regular } from "@fluentui/react-icons";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { SpineSection } from "../hooks/useVariantCRun";
import {
  type CapsuleState,
  buildMiniSpineWindow,
  formatRunTime,
  isRunningSection,
  sectionsNeedingReview,
} from "../runCapsuleModel";
import { formatGutterNumber } from "../sectionLabel";
import type { AgentActivityLine, AgentActivityMap } from "../services/agentActivityFeed";
import {
  DRAFT_STEPS,
  type DraftStep,
  deriveDraftStep,
  deriveSpineProgress,
  sublineDetail,
} from "../services/spineProgress";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";

export interface RunCapsuleProps {
  state: CapsuleState;
  /** The run's spine rows, already scoped to the run (the Spine's list). */
  sections: readonly SpineSection[];
  activeSectionId: string | null;
  /** Per-section live feed (the Spine's newest-action source). */
  agentActivityBySection?: AgentActivityMap;
  /** Run time for the header: elapsed while live, total once settled.
   *  Null hides it. */
  runTimeMs: number | null;
  /** Switch to the Spine tab. */
  onOpenSpine: () => void;
  /** Open one section in the Spine (Inspector). */
  onOpenSection: (sectionId: string) => void;
  onPause?: () => void;
  onResume?: () => void;
  /** The batch's newest run-level step (an activation / trial-context note,
   *  a web search) folded out of the chat, shown as the card's status line
   *  while live. Null / absent hides it. */
  statusLine?: { text: string; at: number } | null;
}

/** Settled card lists at most this many review rows before "+N more". */
const MAX_REVIEW_ROWS = 3;

const useStyles = makeStyles({
  card: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "8px",
    border: `1px solid ${surfaceColors.line}`,
    background: "var(--surface-raised)",
    overflow: "hidden",
    fontFamily: fonts.body,
  },
  header: {
    padding: "10px 12px 10px",
  },
  headerTitle: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: inkColors.ink,
    letterSpacing: "-0.1px",
  },
  headerFailedMeta: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 500,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: statusColors.danger,
  },
  headerTime: {
    marginLeft: "auto",
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 500,
    letterSpacing: "0.04em",
    color: inkColors.ink3,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  statusLine: {
    marginTop: "6px",
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    fontSize: "11.5px",
    color: inkColors.ink3,
    minWidth: 0,
  },
  statusLineText: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  statusLineAge: {
    flexShrink: 0,
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
  },
  headerSummary: {
    marginTop: "3px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    letterSpacing: "0.04em",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  progressTrack: {
    height: "3px",
    marginTop: "8px",
    borderRadius: "2px",
    background: brandColors.light,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: "2px",
    background: brandColors.primary,
    transitionProperty: "width",
    transitionDuration: "300ms",
    transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  },
  progressFillFailed: {
    background: statusColors.danger,
  },
  rows: {
    borderTop: `1px solid ${surfaceColors.line}`,
    "& > :last-child": { borderBottom: "none" },
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "7px 12px",
    background: "none",
    border: "none",
    borderBottom: `1px solid ${surfaceColors.line}`,
    textAlign: "left",
    cursor: "pointer",
    color: "inherit",
    font: "inherit",
    ":hover": { background: brandColors.light },
    "&:focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "-2px" },
    "&:hover [data-row-chevron]": { color: brandColors.primary },
  },
  // Same band as the Spine's running rows: brand tint, 3px rail, brand number.
  rowRunning: {
    background: brandColors.light,
    boxShadow: `inset 3px 0 0 ${brandColors.primary}`,
    borderBottomColor: `color-mix(in srgb, ${brandColors.mid} 25%, transparent)`,
    ":hover": { background: `color-mix(in srgb, ${brandColors.mid} 15%, transparent)` },
    "& [data-gutter]": { color: brandColors.primary, fontWeight: 600 },
  },
  rowInert: {
    cursor: "default",
    ":hover": { background: "none" },
  },
  gutter: {
    flexShrink: 0,
    overflow: "hidden",
    fontFamily: fonts.mono,
    fontSize: "10px",
    letterSpacing: "0.2px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
  },
  label: {
    flex: 1,
    minWidth: 0,
    fontSize: "12px",
    fontWeight: 400,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  labelQueued: { color: inkColors.ink3 },
  labelRunning: { fontWeight: 600, whiteSpace: "normal" },
  rowMeta: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "3px",
    minWidth: 0,
  },
  metaText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 400,
    color: inkColors.ink3,
  },
  rowLog: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    marginTop: "3px",
    minWidth: 0,
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 400,
    color: inkColors.ink3,
  },
  rowLogError: { color: statusColors.danger },
  rowLogText: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowLogAge: {
    flexShrink: 0,
    fontSize: "9.5px",
    color: inkColors.ink5,
    fontVariantNumeric: "tabular-nums",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    height: "15px",
    padding: "0 5px",
    borderRadius: "4px",
    border: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.mono,
    fontSize: "9px",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    lineHeight: 1,
    color: inkColors.ink4,
    whiteSpace: "nowrap",
  },
  chipLive: {
    color: brandColors.primary,
    background: brandColors.tint,
    border: `1px solid ${brandColors.light}`,
  },
  chipApproval: {
    color: inkColors.ink2,
    background: `color-mix(in srgb, ${statusColors.warn} 10%, transparent)`,
    border: `1px solid color-mix(in srgb, ${statusColors.warn} 50%, transparent)`,
  },
  chipDanger: {
    color: statusColors.danger,
    background: `color-mix(in srgb, ${statusColors.danger} 6%, transparent)`,
    border: `1px solid color-mix(in srgb, ${statusColors.danger} 30%, transparent)`,
  },
  steps: {
    display: "inline-flex",
    gap: "2px",
    marginRight: "5px",
    verticalAlign: "middle",
  },
  step: {
    width: "7px",
    height: "3px",
    borderRadius: "2px",
    background: surfaceColors.line,
  },
  stepDone: { background: statusColors.cool },
  stepActive: { background: brandColors.primary },
  rowTime: {
    flexShrink: 0,
    fontFamily: fonts.mono,
    fontSize: "11px",
    fontWeight: 700,
    color: brandColors.primary,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  chevron: {
    flexShrink: 0,
    marginLeft: "-4px",
    marginRight: "-6px",
    color: inkColors.ink5,
  },
  chevronSlot: { flexShrink: 0, width: "12px", marginLeft: "-4px", marginRight: "-6px" },
  // Status mark — the Spine's: spinner / green check / ! / ✕ / empty slot.
  mark: {
    width: "18px",
    height: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  dot: { borderRadius: "50%" },
  dotDrafting: {
    width: "12px",
    height: "12px",
    border: `2px solid ${brandColors.light}`,
    borderTopColor: brandColors.primary,
    boxSizing: "border-box",
    animationName: { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
    animationDuration: "900ms",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  dotStatic: { animation: "none" },
  dotFilled: {
    width: "16px",
    height: "16px",
    color: "var(--surface)",
    fontSize: "10px",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dotDrafted: { background: statusColors.cool },
  dotApproval: { background: statusColors.warn },
  dotFlagged: { background: statusColors.danger },
  dotFailed: {
    width: "14px",
    height: "14px",
    boxSizing: "border-box",
    background: "var(--surface-raised)",
    border: `1.5px solid ${statusColors.danger}`,
    color: statusColors.danger,
    fontSize: "8px",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  gap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "4px 12px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
  },
  gapDots: { color: inkColors.ink5 },
  more: {
    padding: "5px 12px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 8px 6px 12px",
    borderTop: `1px solid ${surfaceColors.line}`,
  },
  spacer: { flex: 1 },
  openSpine: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    padding: "3px 0",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    color: brandColors.primary,
    ":hover": { textDecoration: "underline" },
    "&:focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "2px", borderRadius: "3px" },
  },
  quietBtn: {
    padding: "3px 8px",
    background: "none",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "10.5px",
    fontWeight: 500,
    color: inkColors.ink3,
    ":hover": { color: inkColors.ink, border: `1px solid ${inkColors.ink4}` },
    "&:focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "1px" },
  },
});

/** White check inside the green "done" disc (the Spine's glyph). */
const CHECK_GLYPH = (
  <svg width="9" height="9" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path
      d="M2.5 6.3 5 8.7 9.5 3.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function StatusMark({
  section,
  paused,
  styles,
}: {
  section: SpineSection;
  paused: boolean;
  styles: ReturnType<typeof useStyles>;
}) {
  let dot: React.ReactNode = null;
  if (section.failed && section.status === "flagged") {
    dot = <span className={mergeClasses(styles.dot, styles.dotFailed)}>✕</span>;
  } else if (isRunningSection(section)) {
    dot = <span className={mergeClasses(styles.dot, styles.dotDrafting, paused && styles.dotStatic)} />;
  } else if (section.status === "drafted") {
    dot = <span className={mergeClasses(styles.dot, styles.dotFilled, styles.dotDrafted)}>{CHECK_GLYPH}</span>;
  } else if (section.status === "needs_approval") {
    dot = <span className={mergeClasses(styles.dot, styles.dotFilled, styles.dotApproval)}>!</span>;
  } else if (section.status === "flagged") {
    dot = <span className={mergeClasses(styles.dot, styles.dotFilled, styles.dotFlagged)}>!</span>;
  }
  return (
    <span className={styles.mark} aria-hidden="true">
      {dot}
    </span>
  );
}

/** Chip word + colour for a row that is not drafting (drafting rows show the stage). */
function chipFor(section: SpineSection, styles: ReturnType<typeof useStyles>): { label: string; className: string } {
  if (section.failed && section.status === "flagged") {
    return { label: "Failed", className: mergeClasses(styles.chip, styles.chipDanger) };
  }
  if (section.auditPhase === "running")
    return { label: "Auditing", className: mergeClasses(styles.chip, styles.chipLive) };
  switch (section.status) {
    case "drafting":
      return { label: "Drafting", className: mergeClasses(styles.chip, styles.chipLive) };
    case "needs_approval":
      return { label: "Needs approval", className: mergeClasses(styles.chip, styles.chipApproval) };
    case "flagged":
      return { label: "Flagged", className: mergeClasses(styles.chip, styles.chipDanger) };
    default:
      return { label: "", className: styles.chip };
  }
}

/** A feed line without its "Agent · " prefix (the Spine drops it too). */
function lineBody(line: AgentActivityLine): string {
  const prefix = line.agent ? `${line.agent} · ` : "";
  return prefix && line.text.startsWith(prefix) ? line.text.slice(prefix.length) : line.text;
}

export const RunCapsule: React.FC<RunCapsuleProps> = ({
  state,
  sections,
  activeSectionId,
  agentActivityBySection,
  runTimeMs,
  onOpenSpine,
  onOpenSection,
  onPause,
  onResume,
  statusLine = null,
}) => {
  const styles = useStyles();
  const isLive = state === "running" || state === "paused";
  const paused = state === "paused";
  const progress = deriveSpineProgress(sections, { paused, runFailed: state === "failed" });

  // Stage per running row, only moving forward (LoomSpine's rule: the feed
  // is a short ring buffer and a drafter re-reads sources mid-draft).
  const stepMemoRef = useRef<Map<string, DraftStep>>(new Map());
  const stepMemo = stepMemoRef.current;
  const running = sections.filter(isRunningSection);
  for (const id of [...stepMemo.keys()]) {
    if (!running.some((r) => r.id === id)) stepMemo.delete(id);
  }
  for (const r of running) {
    const step = deriveDraftStep(r, agentActivityBySection?.get(r.id), stepMemo.get(r.id) ?? null);
    if (step) stepMemo.set(r.id, step);
  }

  // A running row's own time, from when the card first saw it running (or
  // its current stage start, if earlier). Same source as the Spine's.
  const startRef = useRef<Map<string, number>>(new Map());
  const starts = startRef.current;
  const clock = Date.now();
  for (const s of sections) {
    if (isRunningSection(s)) {
      if (!starts.has(s.id)) starts.set(s.id, Math.min(clock, s.sublineStartedAt ?? clock));
    } else {
      starts.delete(s.id);
    }
  }

  // One 1s tick for the row timers and action ages while anything runs.
  const [now, setNow] = useState(() => Date.now());
  const ticking = isLive && !paused && running.length > 0;
  useEffect(() => {
    if (!ticking) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ticking]);

  const gutterChars = Math.max(1, ...sections.map((s) => formatGutterNumber(s.number).length));
  const gutterStyle = { width: `min(54px, calc(${gutterChars}ch + 8px))` };

  const newestLineFor = (id: string): AgentActivityLine | null => {
    const lines = agentActivityBySection?.get(id);
    return lines && lines.length > 0 ? lines[lines.length - 1] : null;
  };

  const renderRow = (section: SpineSection) => {
    const live = isRunningSection(section);
    const queued = section.status === "queued" || section.status === "skipped";
    const step = live ? (stepMemo.get(section.id) ?? null) : null;
    const stepIdx = step ? DRAFT_STEPS.findIndex((st) => st.id === step) : -1;
    const chip = chipFor(section, styles);
    const chipLabel = stepIdx >= 0 && step !== "done" ? DRAFT_STEPS[stepIdx].label : chip.label;
    const metaText = live && section.subline ? (stepIdx >= 0 ? sublineDetail(section.subline) : section.subline) : "";
    const newest = live ? newestLineFor(section.id) : null;
    const start = starts.get(section.id);
    const number = formatGutterNumber(section.number);
    return (
      <button
        key={section.id}
        type="button"
        className={mergeClasses(styles.row, queued && styles.rowInert, live && styles.rowRunning)}
        onClick={queued ? undefined : () => onOpenSection(section.id)}
        aria-label={`${number ? `Section ${section.number}: ` : ""}${section.title} (${section.status})`}
        title={queued ? undefined : "Open in the Spine"}
        data-testid="capsule-row"
        data-status={section.status}
      >
        <span className={styles.gutter} style={gutterStyle} data-gutter>
          {number}
        </span>
        <span className={mergeClasses(styles.label, queued && styles.labelQueued, live && styles.labelRunning)}>
          {section.title}
          {chipLabel && (
            <span className={styles.rowMeta}>
              <span className={chip.className} data-testid="capsule-chip">
                {stepIdx >= 0 && step !== "done" && (
                  <span className={styles.steps} data-testid="capsule-steps" title={`Stage: ${chipLabel}`}>
                    {DRAFT_STEPS.filter((st) => st.id !== "done").map((st, k) => (
                      <span
                        key={st.id}
                        data-step-state={k < stepIdx ? "done" : k === stepIdx ? "active" : "pending"}
                        className={mergeClasses(
                          styles.step,
                          k < stepIdx && styles.stepDone,
                          k === stepIdx && styles.stepActive,
                        )}
                      />
                    ))}
                  </span>
                )}
                {chipLabel}
              </span>
              {metaText && (
                <span className={styles.metaText} title={section.subline}>
                  {metaText}
                </span>
              )}
            </span>
          )}
          {newest && (
            <span
              className={mergeClasses(styles.rowLog, newest.kind === "error" && styles.rowLogError)}
              title={newest.text}
              data-testid="capsule-row-log"
            >
              <span className={styles.rowLogText}>{lineBody(newest)}</span>
              <span className={styles.rowLogAge}>{formatRunTime(now - newest.at)}</span>
            </span>
          )}
        </span>
        {live && start !== undefined && <span className={styles.rowTime}>{formatRunTime(now - start)}</span>}
        <StatusMark section={section} paused={paused} styles={styles} />
        {queued ? (
          <span className={styles.chevronSlot} aria-hidden="true" />
        ) : (
          <ChevronRight12Regular className={styles.chevron} data-row-chevron aria-hidden="true" />
        )}
      </button>
    );
  };

  const review = isLive ? [] : sectionsNeedingReview(sections);

  return (
    <div className={styles.card} data-testid="run-capsule" data-state={state}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <span>{progress.label}</span>
          {progress.failed > 0 && <span className={styles.headerFailedMeta}>{progress.failed} failed</span>}
          {runTimeMs !== null && (
            <span className={styles.headerTime} data-testid="capsule-time">
              {isLive ? "elapsed" : "total"} {formatRunTime(runTimeMs)}
            </span>
          )}
        </div>
        {progress.summary && <div className={styles.headerSummary}>{progress.summary}</div>}
        {isLive && statusLine && (
          // biome-ignore lint/a11y/useSemanticElements: one-line live status inside the card
          <div className={styles.statusLine} role="status" data-testid="capsule-status-line">
            <span className={styles.statusLineText} title={statusLine.text}>
              {statusLine.text}
            </span>
            <span className={styles.statusLineAge}>{formatRunTime(Math.max(0, now - statusLine.at))}</span>
          </div>
        )}
        <div
          className={styles.progressTrack}
          title={`${progress.done} of ${progress.total} sections settled`}
          aria-hidden="true"
        >
          <div
            className={mergeClasses(styles.progressFill, state === "failed" && styles.progressFillFailed)}
            style={{ width: `${progress.percent}%` }}
          />
        </div>
      </div>

      {isLive && sections.length > 0 && (
        <div className={styles.rows}>
          {buildMiniSpineWindow(sections, activeSectionId).map((item) =>
            item.kind === "row" ? (
              renderRow(item.section)
            ) : (
              <div key={item.key} className={styles.gap} data-testid="capsule-gap">
                <span className={styles.gapDots} aria-hidden="true">
                  ⋮
                </span>
                {item.label}
              </div>
            ),
          )}
        </div>
      )}

      {review.length > 0 && (
        <div className={styles.rows}>
          {review.slice(0, MAX_REVIEW_ROWS).map(renderRow)}
          {review.length > MAX_REVIEW_ROWS && (
            <div className={styles.more}>+{review.length - MAX_REVIEW_ROWS} more to review</div>
          )}
        </div>
      )}

      <div className={styles.footer}>
        <button type="button" className={styles.openSpine} onClick={onOpenSpine} data-testid="capsule-open-spine">
          {isLive ? "Open Spine" : "Review in Spine"}
          <ChevronRight12Regular aria-hidden="true" />
        </button>
        <span className={styles.spacer} />
        {state === "running" && onPause && (
          <button type="button" className={styles.quietBtn} onClick={onPause}>
            Pause
          </button>
        )}
        {paused && onResume && (
          <button type="button" className={styles.quietBtn} onClick={onResume}>
            Resume
          </button>
        )}
      </div>
    </div>
  );
};

export default RunCapsule;
